// The editor's timeline on the wire (E1, spec E2's version 2): the API's seconds and snake case
// as the editor's own `Timeline` (whole milliseconds) and back. Pure, so the editor's screens
// (through project.ts) and the Save worker's page (`renderer.ts`, spec save-worker) map a saved
// timeline the one same way.
import type {
  CaptionAnimation as WireCaptionAnimation,
  CaptionMotion as WireCaptionMotion,
  CaptionSettings as WireCaptionSettings,
  CaptionTag as WireCaptionTag,
  ClipAnimation,
  EditAspect,
  ActorSource,
  Marker as WireMarker,
  AudioClip as WireAudioClip,
  SourceRef,
  Timeline as WireTimeline,
  TimelineClip as WireClip,
  Track as WireTrack,
  Transition as WireTransition,
  ImageClip as WireImageClip,
  TextStyle as WireTextStyle,
  VideoClip as WireVideoClip,
} from "../../api/generated/model";
import type { Animation } from "./animations";
import { liveFrame } from "./engine/project";
import {
  DEFAULT_CAPTIONS,
  DEFAULT_CAPTION_STYLE,
  type CaptionAnimation,
  type CaptionMotion,
  type CaptionSettings,
  type CaptionTag,
} from "./captions/caption-style";
import { DEFAULT_PARAGRAPH } from "./captions/segment";
import type { EffectValue } from "./effect-filters";
import type { Look } from "./look";
import { addedStyle, titleStyle, type TextStyle } from "./text-style";
import type { Clip, ClipSource, FileSource, Marker, Timeline, Track, Transition, VideoClip } from "./timeline";

const ms = (seconds: number) => Math.round(seconds * 1000);
const seconds = (milliseconds: number) => milliseconds / 1000;

function fromWireSource(source: SourceRef): FileSource {
  return source.kind === "asset"
    ? { kind: "asset", assetId: source.asset_id }
    : { kind: "output", jobId: source.job_id, index: source.output_index };
}

/** A file as the contract names it: a library file, or a job's output (Remove background's start too). */
export function toWireSource(source: FileSource): SourceRef {
  return source.kind === "asset"
    ? { kind: "asset", asset_id: source.assetId }
    : { kind: "job_output", job_id: source.jobId, output_index: source.index };
}

/** A picture's source: a file, or one of the team's actors (#244 part 3). */
function fromWirePicture(source: ActorSource): ClipSource {
  return "actor_id" in source ? { kind: "actor", actorId: source.actor_id } : fromWireSource(source);
}

function toWirePicture(source: ClipSource): ActorSource {
  return source.kind === "actor" ? { kind: "actor", actor_id: source.actorId } : toWireSource(source);
}

/**
 * A video's or a sound's speed curve from the wire (spec clip-speed-curves); a clip saved before
 * curves, or with none, plays its one speed.
 */
function fromWireCurve(clip: WireVideoClip | WireAudioClip): Pick<VideoClip, "speedPoints" | "speedCurve"> {
  const speedPoints = (clip.speed_points ?? []).map((point) => ({ atMs: ms(point.at_s), speed: point.speed }));
  // The contract keeps a name exactly with its points; were one missing, the points play as Custom.
  return { speedPoints, speedCurve: speedPoints.length >= 2 ? (clip.speed_curve ?? "custom") : null };
}

/** A wire clip as the editor's: milliseconds, and the ids the server keeps. */
/** A picture's look from the wire (a clip saved before #241 has none: the plain one). */
function fromWireLook(clip: WireVideoClip | WireImageClip): Look {
  return {
    box: clip.box ?? null,
    rotation: clip.rotation ?? 0,
    flipX: clip.flip_x ?? false,
    flipY: clip.flip_y ?? false,
    opacity: clip.opacity ?? 1,
    radius: clip.radius ?? 0,
    stroke: clip.stroke ?? null,
    shadow:
      clip.shadow == null
        ? null
        : {
            color: clip.shadow.color,
            offsetX: clip.shadow.offset_x,
            offsetY: clip.shadow.offset_y,
            blur: clip.shadow.blur,
          },
    chromaKey: clip.chroma_key ?? null,
  };
}

function toWireLook(look: Look) {
  return {
    box: look.box,
    rotation: look.rotation,
    flip_x: look.flipX,
    flip_y: look.flipY,
    opacity: look.opacity,
    radius: look.radius,
    stroke: look.stroke,
    shadow:
      look.shadow === null
        ? null
        : {
            color: look.shadow.color,
            offset_x: look.shadow.offsetX,
            offset_y: look.shadow.offsetY,
            blur: look.shadow.blur,
          },
    chroma_key: look.chromaKey,
  };
}

/** A text's style from the wire, every value there (the contract's defaults filled in). */
function fromWireStyle(style: WireTextStyle, frame: { width: number; height: number }): TextStyle {
  const added = addedStyle(frame);
  return {
    x: style.x,
    y: style.y,
    width: style.width,
    rotation: style.rotation ?? 0,
    opacity: style.opacity ?? 1,
    font: style.font ?? added.font,
    weight: style.weight ?? added.weight,
    italic: style.italic ?? false,
    size: style.size ?? added.size,
    align: style.align ?? added.align,
    underline: style.underline ?? false,
    overline: style.overline ?? false,
    strikethrough: style.strikethrough ?? false,
    textCase: style.case ?? "as_typed",
    color: style.color === undefined ? added.color : style.color,
    background:
      style.background == null
        ? null
        : {
            color: style.background.color,
            opacity: style.background.opacity ?? 1,
            radius: style.background.radius ?? 4,
            padX: style.background.pad_x ?? 8,
            padY: style.background.pad_y ?? 4,
          },
    stroke: style.stroke ?? null,
    shadow:
      style.shadow == null
        ? null
        : {
            color: style.shadow.color,
            offsetX: style.shadow.offset_x,
            offsetY: style.shadow.offset_y,
            blur: style.shadow.blur,
          },
  };
}

function toWireStyle(style: TextStyle): WireTextStyle {
  return {
    x: style.x,
    y: style.y,
    width: style.width,
    rotation: style.rotation,
    opacity: style.opacity,
    font: style.font,
    weight: style.weight,
    italic: style.italic,
    size: style.size,
    align: style.align,
    underline: style.underline,
    overline: style.overline,
    strikethrough: style.strikethrough,
    case: style.textCase,
    color: style.color,
    background:
      style.background === null
        ? null
        : {
            color: style.background.color,
            opacity: style.background.opacity,
            radius: style.background.radius,
            pad_x: style.background.padX,
            pad_y: style.background.padY,
          },
    stroke: style.stroke,
    shadow:
      style.shadow === null
        ? null
        : {
            color: style.shadow.color,
            offset_x: style.shadow.offsetX,
            offset_y: style.shadow.offsetY,
            blur: style.shadow.blur,
          },
  };
}

/** A clip's animations off the wire, each field at the contract's default where it is left out. */
function fromWireAnimations(animations: readonly ClipAnimation[] | undefined): Animation[] {
  return (animations ?? []).map((each) => ({
    id: each.id,
    preset: each.preset,
    mode: each.mode ?? null,
    durationMs: each.duration_ms ?? 1000,
    delayMs: each.delay_ms ?? 0,
    iterations: each.iterations ?? 1,
    easing: each.easing ?? "linear",
    direction: each.direction ?? "left",
    distance: each.distance ?? 300,
    angle: each.angle ?? 45,
    axis: each.axis ?? "vertical",
    origin: each.origin ?? "center",
    xMargin: each.x_margin ?? 100,
    yMargin: each.y_margin ?? 100,
    borderRadius: each.border_radius ?? 0,
    initial: each.initial ?? 0,
    blockMode: each.block_mode ?? "reveal",
    blockSize: each.block_size ?? 0.3,
    blockColor: each.block_color ?? "#FFFFFF", // raw-color: live's Block Color, a value
    invertedColor: each.inverted_color ?? "#000000", // raw-color: live's Inverted Text, a value
    stagger: each.stagger ?? null,
    keyframes: (each.keyframes ?? []).map((stop) => ({ at: stop.at, values: { ...stop.values } })),
    mirror: each.mirror ?? false,
  }));
}

function toWireAnimation(animation: Animation): ClipAnimation {
  return {
    id: animation.id,
    preset: animation.preset,
    mode: animation.mode,
    duration_ms: animation.durationMs,
    delay_ms: animation.delayMs,
    iterations: animation.iterations,
    easing: animation.easing,
    direction: animation.direction,
    distance: animation.distance,
    angle: animation.angle,
    axis: animation.axis,
    origin: animation.origin,
    x_margin: animation.xMargin,
    y_margin: animation.yMargin,
    border_radius: animation.borderRadius,
    initial: animation.initial,
    block_mode: animation.blockMode,
    block_size: animation.blockSize,
    block_color: animation.blockColor,
    inverted_color: animation.invertedColor,
    stagger: animation.stagger,
    keyframes: animation.keyframes.map((stop) => ({ at: stop.at, values: { ...stop.values } })),
    mirror: animation.mirror,
  };
}

export function fromWireClip(clip: WireClip, frame: { width: number; height: number }): Clip {
  const placed = {
    id: clip.id,
    trackId: clip.track_id,
    startMs: ms(clip.start_s),
    endMs: ms(clip.end_s),
    locked: clip.locked ?? false,
    hidden: clip.hidden ?? false,
  };
  switch (clip.kind) {
    case "video":
      return {
        ...placed,
        kind: "video",
        source: fromWireSource(clip.source),
        inMs: ms(clip.in_s ?? 0),
        speed: clip.speed ?? 1,
        muted: clip.muted ?? false,
        volume: clip.volume ?? 1,
        volumePoints: (clip.volume_points ?? []).map((point) => ({ atMs: ms(point.at_s), volume: point.volume })),
        ...fromWireCurve(clip),
        fadeInMs: clip.fade_in_ms ?? 0,
        fadeOutMs: clip.fade_out_ms ?? 0,
        ...fromWireLook(clip),
        animations: fromWireAnimations(clip.animations),
        // A video saved before Remove background (spec remove-background.md) has no original.
        originalSource: clip.original_source == null ? null : fromWireSource(clip.original_source),
      };
    case "image":
      return {
        ...placed,
        kind: "image",
        source: fromWirePicture(clip.source),
        ...fromWireLook(clip),
        animations: fromWireAnimations(clip.animations),
      };
    case "audio":
      return {
        ...placed,
        kind: "audio",
        source: fromWireSource(clip.source),
        inMs: ms(clip.in_s ?? 0),
        speed: clip.speed ?? 1,
        muted: clip.muted ?? false,
        volume: clip.volume ?? 1,
        volumePoints: (clip.volume_points ?? []).map((point) => ({ atMs: ms(point.at_s), volume: point.volume })),
        ...fromWireCurve(clip),
        fadeInMs: clip.fade_in_ms ?? 0,
        fadeOutMs: clip.fade_out_ms ?? 0,
        // A sound saved before its beats (spec beats.md) has none.
        beatsMs: clip.beats_ms ?? [],
      };
    case "text":
      // A title made before #241 part 2 has no style: it reads as live's text that looks as E1
      // drew it at its place.
      return {
        ...placed,
        kind: "text",
        text: clip.text,
        style:
          clip.style == null ? titleStyle(clip.position ?? "bottom", frame) : fromWireStyle(clip.style, frame),
        animations: fromWireAnimations(clip.animations),
      };
    case "effect":
      // The server holds each value to its kind (a number, a switch, a colour or an X/Y pair).
      return { ...placed, kind: "effect", effect: clip.effect, values: { ...clip.values } as Record<string, EffectValue> };
    case "caption":
      return {
        ...placed,
        kind: "caption",
        words: clip.words.map((word) => ({
          text: word.text,
          startMs: word.start_ms,
          endMs: word.end_ms,
          line: word.line ?? 0,
          emphasis: word.emphasis ?? null,
        })),
        box: { ...clip.box },
        // Saved before a caption had its own scale and turn (#560): upright at the style's size.
        scale: clip.scale ?? 1,
        rotation: clip.rotation ?? 0,
      };
  }
}

/** A caption animation's pose, its unset values at rest. */
function fromWireMotion(motion: WireCaptionMotion): CaptionMotion {
  return {
    opacity: motion.opacity ?? 1,
    scale: motion.scale ?? 1,
    x: motion.x ?? 0,
    y: motion.y ?? 0,
    rotation: motion.rotation ?? 0,
  };
}

function fromWireCaptionAnimation(animation: WireCaptionAnimation | null | undefined): CaptionAnimation | null {
  if (animation == null) return null;
  return {
    preset: animation.preset ?? null,
    scope: animation.scope ?? "word",
    enterFrom: fromWireMotion(animation.enter_from),
    enterTo: fromWireMotion(animation.enter_to),
    durationMs: animation.duration_ms,
    easing: animation.easing ?? "linear",
    active:
      animation.active == null
        ? null
        : {
            scale: animation.active.scale ?? 1,
            y: animation.active.y ?? 0,
            dynamic: animation.active.dynamic ?? false,
          },
  };
}

function toWireCaptionAnimation(animation: CaptionAnimation | null): WireCaptionAnimation | null {
  if (animation === null) return null;
  return {
    preset: animation.preset,
    scope: animation.scope,
    enter_from: { ...animation.enterFrom },
    enter_to: { ...animation.enterTo },
    duration_ms: animation.durationMs,
    easing: animation.easing,
    active: animation.active === null ? null : { ...animation.active },
  };
}

function fromWireTag(tag: WireCaptionTag | null | undefined): CaptionTag | null {
  if (tag == null) return null;
  return {
    color: tag.color,
    font: tag.font ?? null,
    weight: tag.weight ?? 700,
    italic: tag.italic ?? false,
    sizeRatio: tag.size_ratio ?? 1,
    underline: tag.underline ?? false,
    difference: tag.difference ?? false,
    animation: fromWireCaptionAnimation(tag.animation),
  };
}

function toWireTag(tag: CaptionTag | null): WireCaptionTag | null {
  if (tag === null) return null;
  return {
    color: tag.color,
    font: tag.font,
    weight: tag.weight,
    italic: tag.italic,
    size_ratio: tag.sizeRatio,
    underline: tag.underline,
    difference: tag.difference,
    animation: toWireCaptionAnimation(tag.animation),
  };
}

/** The captions' look from the wire: none saved is live's default look (#239), and a part of it
 * not saved (a look saved before #264) its default. */
export function fromWireCaptions(captions: WireCaptionSettings | null | undefined): CaptionSettings {
  if (captions == null) return DEFAULT_CAPTIONS;
  const style = captions.style;
  const plain = DEFAULT_CAPTION_STYLE;
  const paragraph = captions.paragraph;
  return {
    style: {
      font: style.font ?? plain.font,
      weight: style.weight ?? plain.weight,
      italic: style.italic ?? plain.italic,
      size: style.size ?? plain.size,
      textCase: style.case ?? plain.textCase,
      align: style.align ?? plain.align,
      color: style.color === undefined ? plain.color : style.color,
      upcomingColor: style.upcoming_color === undefined ? plain.upcomingColor : style.upcoming_color,
      activeColor: style.active_color ?? null,
      activeBackground: style.active_background ?? null,
      background:
        style.background === undefined
          ? plain.background
          : style.background === null
            ? null
            : {
                color: style.background.color,
                opacity: style.background.opacity ?? 1,
                radius: style.background.radius ?? 4,
                padX: style.background.pad_x ?? 8,
                padY: style.background.pad_y ?? 4,
              },
      stroke: style.stroke ?? null,
      shadow:
        style.shadow == null
          ? null
          : {
              color: style.shadow.color,
              offsetX: style.shadow.offset_x,
              offsetY: style.shadow.offset_y,
              blur: style.shadow.blur,
              alpha: style.shadow.alpha ?? 1,
            },
      letterSpacing: style.letter_spacing ?? 0,
      lineHeight: style.line_height ?? 1,
      underline: style.underline ?? false,
      overline: style.overline ?? false,
      strikethrough: style.strikethrough ?? false,
      positive: fromWireTag(style.positive),
      negative: fromWireTag(style.negative),
    },
    paragraph:
      paragraph == null
        ? DEFAULT_PARAGRAPH
        : {
            ...DEFAULT_PARAGRAPH,
            quantity: paragraph.quantity ?? DEFAULT_PARAGRAPH.quantity,
            frequency: paragraph.frequency ?? DEFAULT_PARAGRAPH.frequency,
            pauseS: paragraph.pause_s ?? DEFAULT_PARAGRAPH.pauseS,
            characterCount: paragraph.character_count ?? DEFAULT_PARAGRAPH.characterCount,
            wordsPerLine: paragraph.words_per_line ?? DEFAULT_PARAGRAPH.wordsPerLine,
            splitS: paragraph.split_s ?? DEFAULT_PARAGRAPH.splitS,
            autofit: paragraph.autofit ?? DEFAULT_PARAGRAPH.autofit,
          },
    placement:
      captions.placement == null
        ? DEFAULT_CAPTIONS.placement
        : {
            horizontal: captions.placement.horizontal ?? "center",
            vertical: captions.placement.vertical ?? "center",
          },
    animation: fromWireCaptionAnimation(captions.animation),
    presetId: captions.preset_id ?? null,
    superSizeNoBreak: captions.super_size_no_break ?? true,
  };
}

/** The captions' look on the wire, every part of it. */
export function toWireCaptions(captions: CaptionSettings): WireCaptionSettings {
  const { style, paragraph, placement } = captions;
  return {
    style: {
      font: style.font,
      weight: style.weight,
      italic: style.italic,
      size: style.size,
      case: style.textCase,
      align: style.align,
      color: style.color,
      upcoming_color: style.upcomingColor,
      active_color: style.activeColor,
      active_background: style.activeBackground,
      background:
        style.background === null
          ? null
          : {
              color: style.background.color,
              opacity: style.background.opacity,
              radius: style.background.radius,
              pad_x: style.background.padX,
              pad_y: style.background.padY,
            },
      stroke: style.stroke,
      shadow:
        style.shadow === null
          ? null
          : {
              color: style.shadow.color,
              offset_x: style.shadow.offsetX,
              offset_y: style.shadow.offsetY,
              blur: style.shadow.blur,
              alpha: style.shadow.alpha,
            },
      letter_spacing: style.letterSpacing,
      line_height: style.lineHeight,
      underline: style.underline,
      overline: style.overline,
      strikethrough: style.strikethrough,
      positive: toWireTag(style.positive),
      negative: toWireTag(style.negative),
    },
    paragraph: {
      quantity: paragraph.quantity,
      frequency: paragraph.frequency,
      pause_s: paragraph.pauseS,
      character_count: paragraph.characterCount,
      words_per_line: paragraph.wordsPerLine,
      split_s: paragraph.splitS,
      autofit: paragraph.autofit,
    },
    placement: { horizontal: placement.horizontal, vertical: placement.vertical },
    animation: toWireCaptionAnimation(captions.animation),
    preset_id: captions.presetId,
    super_size_no_break: captions.superSizeNoBreak,
  };
}

function toWireClip(clip: Clip): WireClip {
  const placed = {
    id: clip.id,
    track_id: clip.trackId,
    start_s: seconds(clip.startMs),
    end_s: seconds(clip.endMs),
    locked: clip.locked,
    hidden: clip.hidden,
  };
  switch (clip.kind) {
    case "video":
      return {
        ...placed,
        kind: "video",
        source: toWireSource(clip.source),
        in_s: seconds(clip.inMs),
        speed: clip.speed,
        muted: clip.muted,
        volume: clip.volume,
        volume_points: clip.volumePoints.map((point) => ({ at_s: seconds(point.atMs), volume: point.volume })),
        speed_points: clip.speedPoints.map((point) => ({ at_s: seconds(point.atMs), speed: point.speed })),
        speed_curve: clip.speedCurve,
        fade_in_ms: clip.fadeInMs,
        fade_out_ms: clip.fadeOutMs,
        ...toWireLook(clip),
        animations: clip.animations.map(toWireAnimation),
        original_source: clip.originalSource === null ? null : toWireSource(clip.originalSource),
      };
    case "image":
      return {
        ...placed,
        kind: "image",
        source: toWirePicture(clip.source),
        ...toWireLook(clip),
        animations: clip.animations.map(toWireAnimation),
      };
    case "audio":
      return {
        ...placed,
        kind: "audio",
        source: toWireSource(clip.source),
        in_s: seconds(clip.inMs),
        speed: clip.speed,
        muted: clip.muted,
        volume: clip.volume,
        volume_points: clip.volumePoints.map((point) => ({ at_s: seconds(point.atMs), volume: point.volume })),
        speed_points: clip.speedPoints.map((point) => ({ at_s: seconds(point.atMs), speed: point.speed })),
        speed_curve: clip.speedCurve,
        fade_in_ms: clip.fadeInMs,
        fade_out_ms: clip.fadeOutMs,
        beats_ms: [...clip.beatsMs],
      };
    case "text":
      return {
        ...placed,
        kind: "text",
        text: clip.text.trim(),
        style: toWireStyle(clip.style),
        animations: clip.animations.map(toWireAnimation),
      };
    case "effect":
      return { ...placed, kind: "effect", effect: clip.effect, values: { ...clip.values } };
    case "caption":
      return {
        ...placed,
        kind: "caption",
        words: clip.words.map((word) => ({
          text: word.text,
          start_ms: word.startMs,
          end_ms: word.endMs,
          line: word.line,
          emphasis: word.emphasis,
        })),
        box: { ...clip.box },
        scale: clip.scale,
        rotation: clip.rotation,
      };
  }
}

/**
 * The wire's timeline as the editor's (spec E2's version 2: tracks of placed clips, and the
 * transitions on their cuts, none in a timeline saved before #240).
 */
export function fromWireTimeline(timeline: WireTimeline, aspect: EditAspect): Timeline {
  return {
    tracks: timeline.tracks.map(fromWireTrack),
    clips: timeline.clips.map((clip) => fromWireClip(clip, liveFrame(aspect))),
    transitions: (timeline.transitions ?? []).map(fromWireTransition),
    captions: fromWireCaptions(timeline.captions),
    aspect,
    markers: (timeline.markers ?? []).map(fromWireMarker),
  };
}

export function fromWireTrack(track: WireTrack): Track {
  return { id: track.id, kind: track.kind, muted: track.muted ?? false, magnet: track.magnet ?? false };
}

export function fromWireTransition(each: WireTransition): Transition {
  return { id: each.id, fromId: each.from_clip_id, toId: each.to_clip_id, kind: each.kind, durationMs: ms(each.duration_s) };
}

export function fromWireMarker(marker: WireMarker): Marker {
  return { id: marker.id, atMs: ms(marker.at_s), label: marker.label, color: marker.color };
}

/** The editor's timeline as the wire's: seconds, the tracks in order. Its ratio is saved beside it,
 * as the project's `aspect`. */
export function toWireTimeline(timeline: Timeline): WireTimeline {
  return {
    version: 2,
    tracks: timeline.tracks.map((track) => ({ id: track.id, kind: track.kind, muted: track.muted, magnet: track.magnet })),
    clips: timeline.clips.map(toWireClip),
    transitions: timeline.transitions.map((each) => ({
      id: each.id,
      from_clip_id: each.fromId,
      to_clip_id: each.toId,
      kind: each.kind,
      duration_s: each.durationMs / 1000,
    })),
    captions: toWireCaptions(timeline.captions),
    markers: timeline.markers.map((marker) => ({
      id: marker.id,
      at_s: seconds(marker.atMs),
      label: marker.label,
      color: marker.color,
    })),
  };
}

/**
 * Whether two timelines save the same, whatever the order of their clips, transitions and markers:
 * a version rebuilt from the project's log against the draft a save of this tab sent (spec
 * live-sync-web.md, decision 5).
 */
export function sameSaved(a: Timeline, b: Timeline): boolean {
  if (a === b) return true;
  if (a.aspect !== b.aspect) return false;
  const one = toWireTimeline(a);
  const other = toWireTimeline(b);
  const keyed = (items: readonly { id: string }[] = []) => new Map(items.map((item) => [item.id, JSON.stringify(item)]));
  const same = (first: Map<string, string>, second: Map<string, string>) =>
    first.size === second.size && [...first].every(([id, json]) => second.get(id) === json);
  return (
    JSON.stringify(one.tracks) === JSON.stringify(other.tracks) &&
    JSON.stringify(one.captions) === JSON.stringify(other.captions) &&
    same(keyed(one.clips), keyed(other.clips)) &&
    same(keyed(one.transitions), keyed(other.transitions)) &&
    same(keyed(one.markers), keyed(other.markers))
  );
}

/** Whether two timelines save the same: their wire timelines and their ratios. */
export function sameContent(a: Timeline, b: Timeline): boolean {
  return a === b || (a.aspect === b.aspect && JSON.stringify(toWireTimeline(a)) === JSON.stringify(toWireTimeline(b)));
}
